import { useEffect, useState } from "react";
import { Paperclip, X as XIcon } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * AttachmentList — renders image attachments inline as thumbnails so
 * agents/requesters can see what's been shared without leaving the
 * ticket. Non-image files stay as clickable chips. Clicking a thumb
 * opens an in-app lightbox; clicking a chip downloads the file (or
 * opens PDF in a new tab).
 */
export function AttachmentList({ attachments, portalToken }) {
  const [busy, setBusy] = useState(null);
  const [imgUrls, setImgUrls] = useState({}); // { attachmentId: objectURL }
  const [lightbox, setLightbox] = useState(null); // { url, filename } or null

  useEffect(() => {
    // Fetch previews for image attachments only — one blob per image,
    // cached in local state so we don't re-fetch on each render.
    let cancelled = false;
    const imgs = (attachments || []).filter((a) => (a.content_type || "").startsWith("image/"));
    imgs.forEach(async (a) => {
      if (imgUrls[a.id]) return;
      try {
        let blob;
        if (portalToken) {
          const r = await fetch(`${BACKEND}/api/portal/t/${portalToken}/attachments/${a.id}`);
          if (!r.ok) throw new Error("preview fetch failed");
          blob = await r.blob();
        } else {
          const r = await api.get(`/attachments/${a.id}`, { responseType: "blob" });
          blob = new Blob([r.data], { type: a.content_type || "image/*" });
        }
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setImgUrls((prev) => ({ ...prev, [a.id]: url }));
      } catch {
        /* silent — chip fallback still renders */
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments, portalToken]);

  // Revoke object URLs on unmount to avoid memory leaks.
  useEffect(() => {
    return () => {
      Object.values(imgUrls).forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!attachments || !attachments.length) return null;

  const sizeK = (n) =>
    n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

  const openAttachment = async (a) => {
    const isImg = (a.content_type || "").startsWith("image/");
    // For images we already have a preview URL — reuse it in the lightbox.
    if (isImg && imgUrls[a.id]) {
      setLightbox({ url: imgUrls[a.id], filename: a.filename });
      return;
    }
    // Portal path is unauthenticated (token embedded in URL) — link directly.
    if (portalToken) {
      window.open(`${BACKEND}/api/portal/t/${portalToken}/attachments/${a.id}`, "_blank", "noreferrer");
      return;
    }
    // Authenticated path: fetch as blob via axios so the JWT rides in the header.
    setBusy(a.id);
    try {
      const r = await api.get(`/attachments/${a.id}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: a.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const isPdf = (a.content_type || "") === "application/pdf";
      if (isPdf) {
        window.open(url, "_blank", "noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = a.filename || "attachment";
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5_000);
      }
    } catch (err) {
      let msg = err?.message || "Couldn't open attachment";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const txt = await data.text();
          try { msg = JSON.parse(txt).detail || txt; } catch { msg = txt || msg; }
        } catch { /* keep default */ }
      } else if (data?.detail) {
        msg = data.detail;
      }
      const status = err?.response?.status;
      toast.error(status ? `${msg} (HTTP ${status})` : msg, { duration: 8000 });
    } finally {
      setBusy(null);
    }
  };

  const images = attachments.filter((a) => (a.content_type || "").startsWith("image/"));
  const others = attachments.filter((a) => !(a.content_type || "").startsWith("image/"));

  return (
    <div className="mt-3 space-y-2">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="attachment-image-grid">
          {images.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => openAttachment(a)}
              className="group relative block rounded-md border border-slate-200 bg-slate-100 overflow-hidden hover:border-[#4f4c90]/50 hover:shadow-sm transition"
              data-testid={`attachment-${a.id}`}
              title={`${a.filename} · ${sizeK(a.size)}`}
            >
              {imgUrls[a.id] ? (
                <img
                  src={imgUrls[a.id]}
                  alt={a.filename}
                  className="max-h-48 max-w-xs object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-32 h-32 flex items-center justify-center text-[10px] text-slate-500 font-mono">
                  loading…
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1 text-[10px] text-white opacity-0 group-hover:opacity-100 transition">
                <span className="truncate block">{a.filename}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      {others.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {others.map((a) => {
            const isBusy = busy === a.id;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => openAttachment(a)}
                disabled={isBusy}
                className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-800 transition-colors disabled:opacity-60"
                data-testid={`attachment-${a.id}`}
              >
                <Paperclip size={12} weight="bold" className="text-[#8B87C8]" />
                <span className="truncate max-w-xs" title={a.filename}>{a.filename}</span>
                <span className="text-[10px] text-slate-500 font-mono">{sizeK(a.size)}</span>
                {isBusy && <span className="text-[10px] text-slate-500">…</span>}
              </button>
            );
          })}
        </div>
      )}

      {lightbox && (
        <div
          role="dialog"
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          data-testid="attachment-lightbox"
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            data-testid="attachment-lightbox-close"
          >
            <XIcon size={20} weight="bold" />
          </button>
          <img
            src={lightbox.url}
            alt={lightbox.filename}
            className="max-h-[90vh] max-w-[90vw] object-contain cursor-default"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/80 font-mono bg-black/40 rounded px-2 py-1">
            {lightbox.filename}
          </div>
        </div>
      )}
    </div>
  );
}
